import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import BaseTable from "../Base/BaseTable";
import CustomerRow from "./CustomerRow";
import CustomerModal from "./CustomerModal";
import AddCustomerModal from "./AddCustomerModal";
import {
  getCustomers,
  updateCustomer,
  deleteCustomer,
} from "../../../../api/customerApi";
import { MEMBERSHIP_LEVEL_OPTIONS } from "../../../../constants/enums";
import { translateMembershipLevel } from "../../../../utils/stranslateUltils";

const CustomerTable = () => {
  const { t } = useTranslation();
  /** -------------------- STATE -------------------- **/
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const STATUS_OPTIONS = MEMBERSHIP_LEVEL_OPTIONS.map(option => ({
    value: option.value,
    label: option.value
      ? translateMembershipLevel(t, option.value)  
      : t("all")                  
  })); 
  /** -------------------- API HANDLERS -------------------- **/
  const loadCustomers = useCallback(async (page, size, keyword, membershipLevel) => {
    try {
      setLoading(true);
      setError(null);

      const searchParams = {
        page: page - 1,
        size: size,
      };

      if (keyword && keyword.trim()) {
        searchParams.keyword = keyword.trim();
      }

      if (membershipLevel && membershipLevel !== "" && membershipLevel !== "all") {
        searchParams.level = membershipLevel;
      }

      const response = await getCustomers(searchParams);
      setCurrentPage(page);
      setPageSize(size);
      setSearchTerm(keyword || "");
      setStatusFilter(membershipLevel || "");

      if (response && Array.isArray(response.content)) {
        setCustomers(response.content);
        setTotalPages(response.totalPages);
        setTotalItems(response.totalElements);
      } else {
        setCustomers([]);
        setTotalPages(0);
        setTotalItems(0);
      }
    } catch (err) {
      setError(t('admin.base_table.error_loading'));
      console.error("Error loading customers:", err);
      setCustomers([]);
      setTotalPages(0);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const updateCustomerHandler = useCallback(async (updatedCustomer) => {
    const response = await updateCustomer(updatedCustomer.customerId, updatedCustomer);
    setCustomers((prev) =>
      prev.map((c) => (c.customerId === response.customerId ? response : c))
    );
    return response;
  }, []);

  const deleteCustomerHandler = useCallback(async (customer) => {
    await deleteCustomer(customer.customerId);
    
    const nextTotal = totalItems - 1;
    const nextTotalPages = Math.ceil(Math.max(nextTotal, 0) / pageSize);
    const targetPage = nextTotalPages > 0 ? Math.min(currentPage, nextTotalPages) : 1;

    setCustomers((prev) => prev.filter((c) => c.customerId !== customer.customerId));
    setTotalItems(Math.max(nextTotal, 0));
    setTotalPages(nextTotalPages);

    if (targetPage !== currentPage) {
      await loadCustomers(targetPage, pageSize, searchTerm, statusFilter);
    }
  }, [pageSize, currentPage, totalItems, searchTerm, statusFilter, loadCustomers]);

  const handleAddCustomerSuccess = useCallback(() => {
    loadCustomers(currentPage, pageSize, searchTerm, statusFilter);
  }, [currentPage, pageSize, searchTerm, statusFilter, loadCustomers]);

  /** -------------------- RENDER FUNCTIONS -------------------- **/
  const renderRow = (customer, actions) => (
    <CustomerRow
      key={customer.customerId}
      customer={customer}
      onShowDetails={actions.onShowDetails}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
    />
  );

  const renderModal = (customer, mode, onSave, onDelete, onClose) => (
    <CustomerModal
      customer={customer}
      mode={mode}
      onSave={onSave}
      onDelete={onDelete}
      onClose={onClose}
    />
  );

  const renderAddModal = (onClose, onSuccess) => (
    <AddCustomerModal
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );

  // Initial load
  React.useEffect(() => {
    loadCustomers(1, 10, "", "");
  }, [loadCustomers]);

  return (
    <BaseTable
      title={t('admin.customer.title')}
      subtitle={t('admin.customer.subtitle')}
      items={customers}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      searchTerm={searchTerm}
      statusFilter={statusFilter}
      columns={[
        t('admin.customer.table.id'),
        t('admin.customer.table.name'),
        t('admin.customer.table.phone'),
        t('admin.customer.table.loyalty_points'),
        t('admin.customer.table.membership'),
        t('admin.customer.table.actions')
      ]}
      renderRow={renderRow}
      renderModal={renderModal}
      renderAddModal={renderAddModal}
      statusOptions={STATUS_OPTIONS}
      onLoadItems={loadCustomers}
      onUpdateItem={updateCustomerHandler}
      onDeleteItem={deleteCustomerHandler}
      onAddItem={handleAddCustomerSuccess}
      showDateFilter={false}
    />
  );
};

export default CustomerTable;